// Row-level review of an asset import: filter by status, pick which rows to
// save, and choose whether earlier imports of the same rows are updated. Used
// by the PRATUS AI review card and the Assets tab import dialog.
import React, { useMemo, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import {
  ASSET_IMPORT_STATUSES,
  ASSET_IMPORT_STATUS_LABELS,
  countByStatus,
  countPlanned,
  filterRows,
  toggleAllVisible,
  toggleRow,
  willFor,
  type AssetImportStatus,
} from '../assetImportReviewModel';
import type { PreviewRow } from '../types';

const STATUS_VARIANT: Record<
  AssetImportStatus,
  React.ComponentProps<typeof Badge>['variant']
> = {
  new: 'green',
  update: 'blue',
  duplicate: 'secondary',
  needs_review: 'amber',
  invalid: 'destructive',
};

const WILL_LABEL = { create: 'Create', update: 'Update', skip: 'Skip' };

interface Props {
  rows: PreviewRow[];
  selected: number[];
  updateExisting: boolean;
  onSelectedChange: (selected: number[]) => void;
  onUpdateExistingChange: (updateExisting: boolean) => void;
}

export const AssetImportRowsTable: React.FC<Props> = ({
  rows,
  selected,
  updateExisting,
  onSelectedChange,
  onUpdateExistingChange,
}) => {
  const [filter, setFilter] = useState<AssetImportStatus | 'all'>('all');

  const statusCounts = useMemo(() => countByStatus(rows), [rows]);
  const planned = countPlanned(rows, selected, updateExisting);
  const visible = filterRows(rows, filter);
  const visibleSelectable = visible.filter((r) => r.selectable);
  const allVisibleSelected =
    visibleSelectable.length > 0 &&
    visibleSelectable.every((r) => selected.includes(r.index));
  const someVisibleSelected = visibleSelectable.some((r) =>
    selected.includes(r.index)
  );

  return (
    <div data-testid="asset-import-rows">
      <div
        className="tw:mb-2 tw:flex tw:flex-wrap tw:gap-1.5"
        role="group"
        aria-label="Filter rows by status"
      >
        <FilterChip
          active={filter === 'all'}
          onClick={() => setFilter('all')}
          label={`All ${rows.length}`}
        />
        {ASSET_IMPORT_STATUSES.filter((s) => statusCounts[s] > 0).map((s) => (
          <FilterChip
            key={s}
            active={filter === s}
            onClick={() => setFilter(s)}
            label={`${ASSET_IMPORT_STATUS_LABELS[s]} ${statusCounts[s]}`}
          />
        ))}
      </div>

      {statusCounts.update > 0 && (
        <label className="tw:mb-2 tw:flex tw:items-center tw:gap-2 tw:text-[13px] tw:text-foreground">
          <Switch
            checked={updateExisting}
            onCheckedChange={(checked: boolean) =>
              onUpdateExistingChange(checked)
            }
            aria-label="Update existing assets"
          />
          Update assets created by an earlier import of these rows
        </label>
      )}

      <div className="tw:max-h-[45vh] tw:overflow-y-auto tw:rounded-md tw:border tw:border-border">
        <table className="tw:w-full tw:border-collapse tw:text-[13px]">
          <thead className="tw:sticky tw:top-0 tw:bg-muted">
            <tr className="tw:text-left tw:text-muted-foreground">
              <th className="tw:w-8 tw:px-2 tw:py-1.5">
                <Checkbox
                  checked={allVisibleSelected}
                  indeterminate={!allVisibleSelected && someVisibleSelected}
                  disabled={visibleSelectable.length === 0}
                  onCheckedChange={() =>
                    onSelectedChange(
                      toggleAllVisible(selected, visible, !allVisibleSelected)
                    )
                  }
                  aria-label="Select all shown rows"
                />
              </th>
              <th className="tw:w-10 tw:px-2 tw:py-1.5 tw:font-medium">Row</th>
              <th className="tw:px-2 tw:py-1.5 tw:font-medium">Asset</th>
              <th className="tw:px-2 tw:py-1.5 tw:font-medium">Status</th>
              <th className="tw:px-2 tw:py-1.5 tw:font-medium">Will</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((row) => {
              const status = row.status as AssetImportStatus;
              const will = willFor(row, selected, updateExisting);
              return (
                <tr
                  key={row.index}
                  className="tw:border-t tw:border-border tw:align-top"
                >
                  <td className="tw:px-2 tw:py-1.5">
                    <Checkbox
                      checked={row.selectable && selected.includes(row.index)}
                      disabled={!row.selectable}
                      onCheckedChange={() =>
                        onSelectedChange(toggleRow(selected, row.index))
                      }
                      aria-label={`Select row ${row.index}`}
                    />
                  </td>
                  <td className="tw:px-2 tw:py-1.5 tw:text-muted-foreground">
                    {row.index}
                  </td>
                  <td className="tw:px-2 tw:py-1.5">
                    <div className="tw:font-medium tw:text-foreground">
                      {row.label}
                    </div>
                    {row.detail && (
                      <div className="tw:text-muted-foreground">
                        {row.detail}
                      </div>
                    )}
                    {!!row.issues?.length && (
                      <ul className="tw:m-0 tw:mt-0.5 tw:list-none tw:p-0 tw:text-[12px] tw:text-warning">
                        {row.issues.map((issue) => (
                          <li key={issue}>{issue}</li>
                        ))}
                      </ul>
                    )}
                  </td>
                  <td className="tw:px-2 tw:py-1.5">
                    <Badge variant={STATUS_VARIANT[status] ?? 'outline'}>
                      {ASSET_IMPORT_STATUS_LABELS[status] ?? row.status}
                    </Badge>
                    {typeof row.score === 'number' && (
                      <div className="tw:mt-0.5 tw:text-[11px] tw:text-muted-foreground">
                        Match {Math.round(row.score)}
                      </div>
                    )}
                  </td>
                  <td className="tw:px-2 tw:py-1.5 tw:text-muted-foreground">
                    {WILL_LABEL[will]}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="tw:mt-2 tw:text-[12px] tw:text-muted-foreground">
        {planned.create} to create · {planned.update} to update · {planned.skip}{' '}
        skipped. Imports can be undone from the Assets tab.
      </p>
    </div>
  );
};

const FilterChip: React.FC<{
  active: boolean;
  label: string;
  onClick: () => void;
}> = ({ active, label, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    className={`tw:rounded-full tw:border tw:px-2.5 tw:py-0.5 tw:text-[12px] ${
      active
        ? 'tw:border-primary tw:bg-primary tw:text-primary-foreground'
        : 'tw:border-border tw:text-foreground tw:hover:border-ring'
    }`}
  >
    {label}
  </button>
);
